import React from 'react';
import { X, Calendar } from 'lucide-react';
import { formatDate, formatTime } from '../../utils/dateUtils';

const SystemLogDetailsModal = ({
  isOpen,
  onClose,
  log,
  getLogTypeColor
}) => {
  if (!isOpen || !log) return null;

  const user = log.user || log.admin;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Log Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Log ID */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Log ID
            </label>
            <p className="font-mono text-sm sm:text-base font-medium text-primary-600">
              #{log.id}
            </p>
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <div>
              {(() => {
                const color = getLogTypeColor(log.log_type);
                return (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
                    {log.log_type === 'admin_action' ? 'Admin Action' : 'User Activity'}
                  </span>
                );
              })()}
            </div>
          </div>

          {/* User/Admin */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              User/Admin
            </label>
            <div>
              <p className="text-sm sm:text-base font-medium text-gray-900">
                {user?.name || 'System'}
              </p>
              {user?.email && (
                <p className="text-xs sm:text-sm text-gray-500">
                  {user.email}
                </p>
              )}
            </div>
          </div>

          {/* Action Type */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Action Type
            </label>
            <p className="text-sm sm:text-base text-gray-900 capitalize">
              {log.action_type?.replace('_', ' ') || 'N/A'}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <p className="text-sm sm:text-base text-gray-900 whitespace-pre-wrap break-words">
              {log.action_description || 'N/A'}
            </p>
          </div>

          {/* Target */}
          {log.target_type && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Target
              </label>
              <p className="text-sm sm:text-base text-gray-900 capitalize">
                {log.target_type}
              </p>
            </div>
          )}

          {/* IP Address */}
          {log.ip_address && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                IP Address
              </label>
              <p className="font-mono text-sm sm:text-base text-gray-900">
                {log.ip_address}
              </p>
            </div>
          )}

          {/* Date */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Date & Time
            </label>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm sm:text-base font-medium text-gray-900">
                  {formatDate(log.created_at, 'medium')}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">
                  {formatTime(log.created_at)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm sm:text-base font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemLogDetailsModal;

